"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { IccSummary } from "@/components/tools/stats-clinical/icc-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import { PreviewTable } from "@/components/tools/stats-research/preview-table";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type IccForm,
  intraclassCorrelations,
} from "@/lib/tool-kit/stats-clinical/icc";
import {
  type HeaderMode,
  parseNumericTable,
} from "@/lib/tool-kit/stats-research/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const INTRACLASS_CORRELATION_CALCULATOR_STRINGS = {
  dataLabel:
    "Ratings — one subject per row, one rater (or measurement occasion) per column",
  dataHint:
    "Paste from a spreadsheet, or separate values with tabs, commas or spaces. A header row and a first column of subject names are optional. A subject with a missing rating is left out, because the ANOVA needs a complete table.",
  dataPlaceholder: "9\t2\t5\t8\n6\t1\t3\t2\n8\t4\t6\t8",
  dataExample:
    "Judge 1\tJudge 2\tJudge 3\tJudge 4\n9\t2\t5\t8\n6\t1\t3\t2\n8\t4\t6\t8\n7\t1\t2\t6\n10\t5\t6\t9\n6\t2\t4\t7",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  formLabel: "Which ICC do you want?",
  formHint:
    "Single-measure forms describe one rater's score; average-measure forms describe the mean of all {k} raters. Absolute agreement penalises raters who differ in level; consistency ignores that.",
  option11: "ICC(1,1) — one-way random, single rater (McGraw & Wong ICC(1))",
  option21:
    "ICC(2,1) — two-way random, absolute agreement, single rater (ICC(A,1))",
  option31: "ICC(3,1) — two-way mixed, consistency, single rater (ICC(C,1))",
  option1k:
    "ICC(1,k) — one-way random, average of k raters (McGraw & Wong ICC(k))",
  option2k:
    "ICC(2,k) — two-way random, absolute agreement, average of k raters (ICC(A,k))",
  option3k:
    "ICC(3,k) — two-way mixed, consistency, average of k raters (ICC(C,k))",
  levelLabel: "Confidence level",
  subjectsRead: "one: {count} subject|other: {count} subjects",
  ratersRead: "one: {count} rater|other: {count} raters",
  droppedRows:
    "one: {count} subject with a missing rating was left out.|other: {count} subjects with a missing rating were left out.",
  unreadable: "Line {line}, column {column}: “{text}” is not a number.",
  tableCaption: "The ratings as read (first rows)",
  subjectHead: "Subject",
  subjectName: "Subject {number}",
  raterName: "Rater {number}",
  needMore:
    "An ICC needs at least two subjects and two raters, and the ratings cannot all be identical.",
  enterPrompt:
    "Paste a subjects × raters table to see the intraclass correlation, its confidence interval and the ANOVA behind it.",
  results: "Results",
  fTest: "F({df1}, {df2}) for ICC = 0",
  columnInterval: "{level}% CI",
  interpretation:
    "By Koo and Li's guideline, ICC = {icc} is {band} reliability; the interval runs from {lowerBand} to {upperBand}.",
  interpretationNote:
    "Koo and Li (2016) judge reliability by the confidence interval's bounds, not the estimate alone: below 0.5 poor, 0.5–0.75 moderate, 0.75–0.9 good, above 0.9 excellent.",
  bandPoor: "poor",
  bandModerate: "moderate",
  bandGood: "good",
  bandExcellent: "excellent",
  name11: "ICC(1,1)",
  name21: "ICC(2,1)",
  name31: "ICC(3,1)",
  name1k: "ICC(1,k)",
  name2k: "ICC(2,k)",
  name3k: "ICC(3,k)",
  mw11: "ICC(1)",
  mw21: "ICC(A,1)",
  mw31: "ICC(C,1)",
  mw1k: "ICC(k)",
  mw2k: "ICC(A,k)",
  mw3k: "ICC(C,k)",
  describe11: "One-way random, single",
  describe21: "Two-way, absolute agreement, single",
  describe31: "Two-way, consistency, single",
  describe1k: "One-way random, average",
  describe2k: "Two-way, absolute agreement, average",
  describe3k: "Two-way, consistency, average",
  allFormsHeading: "All six forms",
  columnShrout: "Shrout & Fleiss",
  columnMcGraw: "McGraw & Wong",
  columnModel: "Model and type",
  columnIcc: "ICC",
  columnBand: "Koo & Li",
  anovaHeading: "Two-way ANOVA behind the ICC",
  columnSource: "Source",
  columnSs: "Sum of squares",
  columnDf: "df",
  columnMs: "Mean square",
  sourceSubjects: "Between subjects (MSR)",
  sourceRaters: "Between raters (MSC)",
  sourceError: "Residual (MSE)",
  sourceWithin: "Within subjects (MSW)",
  howCalculated: "How this was calculated",
  formula11:
    "ICC(1,1) = (MSR − MSW) / (MSR + (k − 1)·MSW) = ({msr} − {msw}) / ({msr} + {km1}·{msw}) = {icc}",
  formula21:
    "ICC(2,1) = (MSR − MSE) / (MSR + (k − 1)·MSE + k·(MSC − MSE)/n) = ({msr} − {mse}) / ({msr} + {km1}·{mse} + {k}·({msc} − {mse})/{n}) = {icc}",
  formula31:
    "ICC(3,1) = (MSR − MSE) / (MSR + (k − 1)·MSE) = ({msr} − {mse}) / ({msr} + {km1}·{mse}) = {icc}",
  formula1k: "ICC(1,k) = (MSR − MSW) / MSR = ({msr} − {msw}) / {msr} = {icc}",
  formula2k:
    "ICC(2,k) = (MSR − MSE) / (MSR + (MSC − MSE)/n) = ({msr} − {mse}) / ({msr} + ({msc} − {mse})/{n}) = {icc}",
  formula3k: "ICC(3,k) = (MSR − MSE) / MSR = ({msr} − {mse}) / {msr} = {icc}",
  workingF: "F = {f} on {df1} and {df2} degrees of freedom, p {p}",
  workingIntervals:
    "Confidence intervals are F-based: Shrout and Fleiss (1979) for the one-way and consistency forms, McGraw and Wong's (1996) Satterthwaite degrees of freedom for the absolute-agreement forms. Average-measure limits follow from the single-measure limits by the Spearman–Brown step.",
  report:
    "{form} = {icc}, {level}% CI [{lower}, {upper}], F({df1}, {df2}) = {f}, p {p}",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

const FORM_OPTION_KEYS = {
  "1,1": "option11",
  "2,1": "option21",
  "3,1": "option31",
  "1,k": "option1k",
  "2,k": "option2k",
  "3,k": "option3k",
} as const;

const LEVELS = ["0.9", "0.95", "0.99"] as const;

export default function IntraclassCorrelationCalculatorWidget() {
  const strings = useToolUi(INTRACLASS_CORRELATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [header, setHeader] = useState<HeaderMode>("auto");
  const [form, setForm] = useState<IccForm>("2,1");
  const [level, setLevel] = useState("0.95");

  const parsed = useMemo(() => parseNumericTable(text, header), [text, header]);
  const complete = parsed.rows
    .map((row) =>
      row.some((cell) => cell === null) ? null : (row as number[])
    )
    .filter((row): row is number[] => row !== null);
  const dropped = parsed.rows.length - complete.length;
  const result =
    parsed.unreadable.length === 0
      ? intraclassCorrelations(complete, Number(level))
      : null;
  const entered = text.trim() !== "";
  const plural = (
    key: "subjectsRead" | "ratersRead" | "droppedRows",
    n: number
  ) => tPlural(strings[key], locale, n, { count: fmt(n) }).trim();
  const [firstProblem] = parsed.unreadable;

  const preview = complete.slice(0, 8).map((row) => row.map((v) => fmt(v)));
  const head = Array.from(
    { length: parsed.columns },
    (_, j) => parsed.header?.[j] || t(strings.raterName, { number: fmt(j + 1) })
  );

  return (
    <div className="space-y-6">
      <PasteBox
        actions={[
          {
            label: strings.loadExample,
            onClick: () => {
              setHeader("auto");
              setText(strings.dataExample);
            },
          },
          { label: strings.clear, onClick: () => setText("") },
        ]}
        header={{
          label: strings.headerLabel,
          value: header,
          onChange: setHeader,
          options: [
            { value: "auto", label: strings.headerAuto },
            { value: "yes", label: strings.headerYes },
            { value: "no", label: strings.headerNo },
          ],
        }}
        hint={strings.dataHint}
        id="icc-data"
        label={strings.dataLabel}
        onChange={setText}
        placeholder={strings.dataPlaceholder}
        rows={8}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <SelectField
            hint={t(strings.formHint, { k: fmt(parsed.columns || 0) })}
            id="icc-form"
            label={strings.formLabel}
            onChange={setForm}
            options={(
              Object.keys(FORM_OPTION_KEYS) as (keyof typeof FORM_OPTION_KEYS)[]
            ).map((value) => ({
              value,
              label: strings[FORM_OPTION_KEYS[value]],
            }))}
            value={form}
          />
        </div>
        <SelectField
          id="icc-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={LEVELS.map((value) => ({
            value,
            label: `${fmt(Number(value) * 100)}%`,
          }))}
          value={level}
        />
      </div>
      {firstProblem ? (
        <ErrorNotice>
          {t(strings.unreadable, {
            line: fmt(firstProblem.line),
            column: fmt(firstProblem.column),
            text: firstProblem.text,
          })}
        </ErrorNotice>
      ) : null}
      {entered && complete.length > 0 && !firstProblem ? (
        <section className="space-y-2">
          <p className={HINT}>
            {[
              plural("subjectsRead", complete.length),
              plural("ratersRead", parsed.columns),
              dropped > 0 ? plural("droppedRows", dropped) : "",
            ]
              .filter((part) => part !== "")
              .join(" · ")}
          </p>
          <PreviewTable
            caption={strings.tableCaption}
            corner={strings.subjectHead}
            head={head}
            rowHeads={preview.map((_, i) =>
              t(strings.subjectName, { number: fmt(i + 1) })
            )}
            rows={preview}
          />
        </section>
      ) : null}
      {result ? (
        <IccSummary
          fmt={fmt}
          result={result}
          selected={form}
          strings={strings}
        />
      ) : null}
      {entered && !result && !firstProblem ? (
        <ErrorNotice>{strings.needMore}</ErrorNotice>
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}
